import * as React from 'react';
import { useSelector } from 'react-redux';
import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { useRef } from 'react';

import { withDeviceType, IWithDeviceTypeProps } from '../../utils/withDeviceType';
import { SECTION_BACKGROUND } from './constants';
import { trackSectionViewed } from './tracking';
import { Page } from '../../components/Page';
import { Section } from '../../components/Section';
import { HeroSectionContainer } from '../HeroSectionContainer';
import { CustomerRoleSectionContainer } from '../CustomerRoleSectionContainer';
import { DealFlexibilitySectionContainer } from '../DealFlexibilitySectionContainer';
import { DealPriceCalculatorContainer } from '../DealPriceCalculatorContainer';
import { FAQSectionContainer } from '../FAQSectionContainer';
import { PreFooterContainer } from '../PreFooterContainer';
import { AboutServicePresentationSectionContainer } from '../AboutServicePresentationSectionContainer';
import { BonusesSectionContainer } from '../BonusesSectionContainer';
import { DealStepsSectionContainer } from '../DealStepsSectionContainer';
import { AdditionalServicesSectionContainer } from '../AdditionalServicesSectionContainer';
import { CheckDealSectionContainer } from '../CheckDealSectionContainer';
import { CommonDealDetailsModalContainer } from '../CommonDealDetailsModalContainer';
import { CallMeBannerSection } from '../CallMeBannerSection';
import { SignupForMasterclassSection } from '../SignupForMasterclassSection';
import { DealPriceSectionContainer } from '../DealPriceSectionContainer';
import { ConsultationRequestModalContainer } from '../ConsultationRequestModalContainer';
import { selectPromoCalculator } from '../../selectors/promoCalculator';
import { ChatContainer } from '../ChatContainer';
import { FreedomRoutineContainer } from '../FreedomRoutineContainer';

/**
 * Компоновщик секций страницы. Задает отступы между отдельными секциями.
 */
const PageContainer: React.FC<React.PropsWithChildren<IWithDeviceTypeProps>> = ({ isDesktop, deviceType }) => {
  const additionalServicesRef = useRef<HTMLElement | null>(null);
  const promoCalculatorData = useSelector(selectPromoCalculator);
  const scrollToAdditionalServices = () => additionalServicesRef.current?.scrollIntoView({ behavior: 'smooth' });

  const asideContent = React.useMemo(
    () => (
      <>
        <ChatContainer />
        <CommonDealDetailsModalContainer />
        <ConsultationRequestModalContainer requestSentFrom="Промо-страница для б-клиентов" />
      </>
    ),
    [],
  );

  return (
    <Page asideContent={asideContent}>
      <Section
        dataName="HeroSectionContainer"
        withVerticalMargins={false}
        withHorizontalPaddings={false}
        withMaxWidth={false}
        withPositionRelative
        background={SECTION_BACKGROUND.hero[deviceType]}
      >
        <HeroSectionContainer />
      </Section>

      <Section dataName="CustomerRoleSectionContainer" withVerticalMargins={false} withHorizontalPaddings={isDesktop}>
        <CustomerRoleSectionContainer />
      </Section>

      <ActionAfterViewed callback={() => trackSectionViewed('deal_type')}>
        <Section
          dataName="DealFlexibilitySectionContainer"
          background={SECTION_BACKGROUND.dealFlexibility[deviceType]}
          withVerticalMargins={false}
        >
          <DealFlexibilitySectionContainer />
        </Section>
      </ActionAfterViewed>

      <Section dataName="FreedomRoutineSectionContainer" withHorizontalPaddings={isDesktop}>
        <FreedomRoutineContainer />
      </Section>

      <BonusesSectionContainer />

      <ActionAfterViewed callback={() => trackSectionViewed('deal_process')}>
        <Section dataName="DealStepsSectionContainer">
          <DealStepsSectionContainer />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('call_me')}>
        <Section dataName="CallMeBannerSection">
          <CallMeBannerSection />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('deal_price')}>
        <Section dataName="DealPriceSectionContainer">
          {promoCalculatorData ? (
            <DealPriceCalculatorContainer
              scrollToAdditionalServices={scrollToAdditionalServices}
              promoCalculatorData={promoCalculatorData}
            />
          ) : (
            <DealPriceSectionContainer />
          )}
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('services')}>
        <Section dataName="AdditionalServicesSectionContainer" forwardRef={additionalServicesRef}>
          <AdditionalServicesSectionContainer />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('webinar')}>
        <Section dataName="SignupForMasterclassSection">
          <SignupForMasterclassSection />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('FAQ')}>
        <Section dataName="FAQSectionContainer">
          <FAQSectionContainer />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('presentation')}>
        <Section dataName="AboutServicePresentationSectionContainer">
          <AboutServicePresentationSectionContainer />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('basement')}>
        <Section
          dataName="CheckDealSectionContainer"
          withVerticalMargins={false}
          background={SECTION_BACKGROUND.checkDeal[deviceType]}
        >
          <CheckDealSectionContainer />
        </Section>
      </ActionAfterViewed>

      <ActionAfterViewed callback={() => trackSectionViewed('contact_information')}>
        <PreFooterContainer />
      </ActionAfterViewed>
    </Page>
  );
};

const PageContainerWithDeviceType = withDeviceType(PageContainer);
export { PageContainerWithDeviceType as PageContainer };
